// The app is a single page: moving between pages never reloads it, so a tab
// opened before a deploy keeps running the old code until someone forces a
// refresh. This compares the build the tab is running with the one index.html
// now names. When they differ it offers a reload, and reloads by itself on the
// next page change (the user is leaving the page anyway, so nothing half-typed
// is lost). It never reloads in the middle of a page.
const BUILD = /\/static\/js\/main\.[0-9a-f]+\.chunk\.js/;
const CHECK_EVERY_MS = 5 * 60 * 1000;

const runningBuild = (): string | undefined => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  const match = (script?.getAttribute("src") || "").match(BUILD);
  return match ? match[0] : undefined;
};

const deployedBuild = async (): Promise<string | undefined> => {
  const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) return undefined;
  const match = (await res.text()).match(BUILD);
  return match ? match[0] : undefined;
};

const showBar = () => {
  if (document.getElementById("new-version-bar")) return;
  const bar = document.createElement("div");
  bar.id = "new-version-bar";
  bar.setAttribute("role", "status");
  bar.style.cssText =
    "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:100000;" +
    "display:flex;gap:12px;align-items:center;padding:10px 16px;border-radius:999px;" +
    "background:#111;color:#fff;font:14px/1.2 Inter,Arial,sans-serif;" +
    "box-shadow:0 6px 24px rgba(0,0,0,.25);max-width:calc(100vw - 32px)";
  bar.textContent = "A new version is available.";
  const button = document.createElement("button");
  button.textContent = "Reload";
  button.style.cssText =
    "border:0;border-radius:999px;padding:6px 12px;background:#fff;color:#111;" +
    "font:600 13px Inter,Arial,sans-serif;cursor:pointer";
  button.onclick = () => window.location.reload();
  bar.appendChild(button);
  document.body.appendChild(bar);
};

export const reloadOnNewVersion = () => {
  if (process.env.NODE_ENV !== "production") return;
  const running = runningBuild();
  if (!running) return;

  let stale = false;
  const check = async () => {
    if (stale) return;
    try {
      const deployed = await deployedBuild();
      if (deployed && deployed !== running) {
        stale = true;
        showBar();
      }
    } catch (error) {
      // offline or a blip: try again next time
    }
  };

  // Reload on the next move to another section once a newer build is known.
  // Only the first path segment counts: the period (?start=...) and an open
  // post (/content-plan-feed/<id>/...) also change the URL, mid-task.
  // (Share links are /public/<section>/..., so there it is the second.)
  const section = () => {
    const parts = window.location.pathname.split("/");
    return parts[1] === "public" ? `public/${parts[2] || ""}` : parts[1] || "";
  };
  let lastSection = section();
  const reloadIfStale = () => {
    const now = section();
    const moved = now !== lastSection;
    lastSection = now;
    if (stale && moved) window.location.reload();
  };
  const push = window.history.pushState;
  window.history.pushState = function (...args: any[]) {
    push.apply(window.history, args as any);
    reloadIfStale();
  };
  window.addEventListener("popstate", reloadIfStale);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.setInterval(check, CHECK_EVERY_MS);
};
